import type { ApprovalKindLadder } from '@chukua/common';
import { ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Navigate } from 'react-router';
import { CardGridSkeleton } from '../../components/CardGridSkeleton';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { useRolesCatalogue } from '../roles/use-roles-catalogue';
import { EditLadderDrawer } from './EditLadderDrawer';
import { LadderCard } from './LadderCard';
import { useApprovalThresholds } from './use-approval-thresholds';
import { sectionTrail } from '../../layouts/section-trail';

export function ApprovalThresholdsPage() {
  const { state, saveError, saving, save, resetSaveError } = useApprovalThresholds();
  const rolesCatalogue = useRolesCatalogue();
  const [editing, setEditing] = useState<ApprovalKindLadder | null>(null);

  if (state.status === 'forbidden') return <Navigate to="/" replace />;

  const roleOptions = rolesCatalogue.status === 'loaded' ? rolesCatalogue.roles : [];
  const roleName = (key: string) => roleOptions.find((role) => role.key === key)?.name ?? key;

  return (
    <>
      <PageHeader
        breadcrumbs={sectionTrail('approval-thresholds')}
        title="Approval thresholds"
        description="Who approves what, by amount. Above the top tier, only Admin approves."
      />
      {state.status === 'error' && (
        <EmptyState
          icon={ShieldAlert}
          title="Couldn't load approval thresholds"
          description="Something went wrong loading the approval kinds. Try reloading the page."
        />
      )}
      {state.status === 'loading' && <CardGridSkeleton label="Loading approval thresholds" />}
      {state.status === 'loaded' && (
        <div className="grid gap-4 sm:grid-cols-2">
          {state.ladders.map((ladder) => (
            <LadderCard
              key={ladder.key}
              ladder={ladder}
              roleName={roleName}
              onEdit={() => {
                resetSaveError();
                setEditing(ladder);
              }}
            />
          ))}
        </div>
      )}
      <EditLadderDrawer
        ladder={editing}
        roleOptions={roleOptions}
        error={saveError}
        submitting={saving}
        onSave={async (tiers) => {
          if (!editing) return false;
          const saved = await save(editing.key, tiers);
          if (saved) setEditing(null);
          return saved;
        }}
        onClose={() => setEditing(null)}
      />
    </>
  );
}
